'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';

import { createClient } from '@/lib/supabase/client';
import MfaVerify from '@/components/MfaVerify';
import MfaEnroll from '@/components/MfaEnroll';

type LoginStep = 'credentials' | 'mfa-verify' | 'mfa-enroll';

// Roles allowed to use this portal
const ACTIVENET_ROLES = ['contact_centre', 'claims', 'engineer', 'finance', 'claims_manager', 'activenet'];

export default function ActiveNetLoginPage() {
  const router = useRouter();
  const supabase = createClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState<LoginStep>('credentials');
  const [mfaFactorId, setMfaFactorId] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [pendingToken, setPendingToken] = useState('');

  const logLoginAttempt = async (
    userId: string | null,
    success: boolean,
    failureReason?: string,
    status?: string
  ) => {
    try {
      await fetch('/api/security/log-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email,
          success,
          failureReason: failureReason ?? null,
          portalLogin: false,
          userAgent: navigator.userAgent,
          status: status ?? (success ? 'ok' : 'failed'),
        }),
      });
    } catch {
      // Non-critical
    }
  };

  const recordRateLimitAttempt = async (success: boolean) => {
    try {
      await fetch('/api/security/rate-limit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ success }),
      });
    } catch {
      // Non-critical
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // ── Step 1: Check region access ──────────────────────────────────────
      const accessRes = await fetch(
        `/api/security/check-access?email=${encodeURIComponent(email)}`
      );
      const accessData = await accessRes.json();
      if (!accessData.allowed) {
        setError('Access denied: your region is not permitted to access this platform.');
        setLoading(false);
        return;
      }

      // ── Step 2: Check rate limit / lockout ───────────────────────────────
      const rateLimitRes = await fetch('/api/security/rate-limit');
      const rateLimitData = await rateLimitRes.json();
      if (!rateLimitData.allowed) {
        const reason =
          rateLimitData.reason === 'locked_out'
            ? `Your IP has been temporarily locked out due to repeated failed login attempts. Please try again in ${Math.ceil((rateLimitData.retryAfterSeconds ?? 900) / 60)} minutes.`
            : `Too many login attempts. Please wait ${rateLimitData.retryAfterSeconds ?? 300} seconds before trying again.`;
        await logLoginAttempt(null, false, reason, 'rate_limited');
        setError(reason);
        setLoading(false);
        return;
      }

      // ── Step 3: Sign in via Supabase ─────────────────────────────────────
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        await recordRateLimitAttempt(false);
        await logLoginAttempt(null, false, signInError.message, 'failed');
        setError(signInError.message || 'Invalid credentials. Please try again.');
        setLoading(false);
        return;
      }

      const userId = data?.user?.id ?? null;
      const token = data?.session?.access_token ?? null;
      const refreshToken = data?.session?.refresh_token ?? null;

      // ── Step 3b: Persist session server-side via Set-Cookie headers ───────
      if (token && refreshToken) {
        try {
          await fetch('/api/auth/set-session', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token: token, refresh_token: refreshToken }),
          });
          // Small delay to allow the browser to commit the Set-Cookie headers
          await new Promise((resolve) => setTimeout(resolve, 150));
        } catch {
          // Non-critical — fall back to client-side cookie
        }
      }

      // ── Step 4: Client-side role check — query user_profiles directly ────
      // The authenticated session is now active on the client, so we can read
      // the user's own profile row without any server-side Admin API calls.
      // This avoids the Auth Admin API failures seen on production.
      if (userId) {
        let profile: { role: string; is_active: boolean | null } | null = null;
        let profileError: string | null = null;

        // Primary: look up by auth user ID
        const { data: profileById, error: errById } = await supabase
          .from('user_profiles')
          .select('role, is_active')
          .eq('id', userId)
          .single();

        if (!errById && profileById) {
          profile = profileById;
        } else {
          // Fallback: look up by email (handles ID mismatch edge cases)
          const { data: profileByEmail, error: errByEmail } = await supabase
            .from('user_profiles')
            .select('role, is_active')
            .ilike('email', email.toLowerCase().trim())
            .single();

          if (!errByEmail && profileByEmail) {
            profile = profileByEmail;
          } else {
            profileError = errByEmail?.message ?? errById?.message ?? 'Profile not found';
          }
        }

        if (!profile) {
          await supabase.auth.signOut();
          await logLoginAttempt(
            userId,
            false,
            `ActiveNet access denied: profile_not_found — ${profileError}`,
            'failed'
          );
          setError('Your account profile could not be found. Please contact your administrator.');
          setLoading(false);
          return;
        }

        const isActive = profile.is_active !== false; // treat null as active
        if (!isActive) {
          await supabase.auth.signOut();
          await logLoginAttempt(userId, false, 'ActiveNet access denied: account_inactive', 'failed');
          setError('Your account is inactive. Please contact your administrator.');
          setLoading(false);
          return;
        }

        if (!ACTIVENET_ROLES.includes(profile.role)) {
          await supabase.auth.signOut();
          await logLoginAttempt(
            userId,
            false,
            `ActiveNet access denied: role_not_permitted (role: ${profile.role})`,
            'failed'
          );
          setError('Your account is not authorised to access ActiveNet. Please use the standard login portal.');
          setLoading(false);
          return;
        }

        // Cache the verified role in sessionStorage for immediate Header rendering
        try {
          sessionStorage.setItem('activenet_user_role', profile.role);
        } catch {}
      }

      // Successful auth — reset rate limit counter
      await recordRateLimitAttempt(true);

      // ── Step 5: Check MFA requirement ────────────────────────────────────
      if (token) {
        const mfaRes = await fetch('/api/security/mfa-verify', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mfaData = await mfaRes.json();

        if (mfaData.mfaRequired) {
          setPendingUserId(userId ?? '');
          setPendingToken(token);

          if (mfaData.totpEnrolled && mfaData.factorId) {
            setMfaFactorId(mfaData.factorId);
            setStep('mfa-verify');
          } else {
            setStep('mfa-enroll');
          }
          setLoading(false);
          return;
        }
      }

      // No MFA required — log success and redirect
      await logLoginAttempt(userId, true, undefined, 'ok');
      window.location.href = '/activenet/dashboard';
    } catch (err: any) {
      await recordRateLimitAttempt(false);
      await logLoginAttempt(null, false, err?.message, 'failed');
      setError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = async () => {
    await logLoginAttempt(pendingUserId || null, true, undefined, 'ok');
    window.location.href = '/activenet/dashboard';
  };

  const handleMfaEnrollComplete = async () => {
    await logLoginAttempt(pendingUserId || null, true, undefined, 'ok');
    window.location.href = '/activenet/dashboard';
  };

  const handleMfaCancel = async () => {
    await supabase.auth.signOut();
    setStep('credentials');
    setEmail('');
    setPassword('');
    setMfaFactorId('');
    setPendingUserId('');
    setPendingToken('');
  };

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #eff6ff 50%, #f0fdf4 100%)' }}
    >
      {/* Subtle background shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full blur-3xl"
          style={{ background: 'rgba(34,197,94,0.08)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: 'rgba(59,130,246,0.07)' }}
        />
        <div
          className="absolute top-1/3 left-0 w-[350px] h-[350px] rounded-full blur-3xl"
          style={{ background: 'rgba(16,185,129,0.06)' }}
        />
      </div>

      <div className="relative w-full max-w-md">
        {/* Branding */}
        <div className="flex flex-col items-center mb-8 gap-3">
          {/* TTS360 Logo */}
          <div className="flex items-center justify-center mb-1">
            <Image
              src="/assets/images/TTS360_logo-1779956897072.png"
              alt="TTS360 Logo"
              width={160}
              height={60}
              className="object-contain"
              priority
            />
          </div>
          <h1 className="font-display text-3xl font-bold mt-1 text-center tracking-tight" style={{ color: '#0f172a' }}>
            Login to{' '}
            <span style={{ color: '#16a34a' }}>Activenet</span>
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider border px-2.5 py-1 rounded-full"
              style={{
                color: '#0284c7',
                borderColor: 'rgba(2,132,199,0.35)',
                background: 'rgba(2,132,199,0.08)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              Operations Portal
            </span>
          </div>
          <p className="text-sm text-center mt-1" style={{ color: '#64748b' }}>
            Claims · Contact Centre · Engineering · Finance
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8 border"
          style={{
            background: '#ffffff',
            borderColor: 'rgba(22,163,74,0.15)',
            boxShadow: '0 4px 40px rgba(22,163,74,0.08), 0 1px 8px rgba(0,0,0,0.06)',
          }}
        >
          {step === 'credentials' && (
            <>
              <h2 className="font-display text-2xl font-bold mb-1" style={{ color: '#0f172a' }}>
                Sign in
              </h2>
              <p className="text-sm mb-6" style={{ color: '#64748b' }}>
                Enter your credentials to access Activenet.
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: '#475569' }}
                  >
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@tts360.co.uk"
                    autoComplete="email"
                    className="w-full rounded-lg px-4 py-3 text-sm focus:outline-none transition-all duration-200"
                    style={{
                      background: '#f8fafc',
                      border: '1.5px solid #e2e8f0',
                      color: '#0f172a',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#16a34a';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    required
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: '#475569' }}
                    >
                      Password
                    </label>
                    <Link
                      href="/forgot-password"
                      className="text-xs font-medium hover:underline transition-colors"
                      style={{ color: '#0284c7' }}
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className="w-full rounded-lg px-4 py-3 pr-11 text-sm focus:outline-none transition-all duration-200"
                      style={{
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        color: '#0f172a',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#16a34a';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-200"
                      style={{ color: '#94a3b8' }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div
                    className="flex items-start gap-2.5 px-4 py-3 rounded-lg"
                    style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                    }}
                  >
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className="text-xs leading-relaxed" style={{ color: '#dc2626' }}>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-lg text-sm font-semibold tracking-wide uppercase mt-1 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white transition-all duration-200 hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, #16a34a, #0284c7)',
                    boxShadow: '0 4px 16px rgba(22,163,74,0.3)',
                  }}
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in…
                    </>
                  ) : (
                    'Sign in to Activenet'
                  )}
                </button>
              </form>
            </>
          )}

          {step === 'mfa-verify' && mfaFactorId && (
            <MfaVerify
              factorId={mfaFactorId}
              accessToken={pendingToken}
              onSuccess={handleMfaSuccess}
              onCancel={handleMfaCancel}
            />
          )}

          {step === 'mfa-enroll' && (
            <MfaEnroll onComplete={handleMfaEnrollComplete} />
          )}
        </div>

        <p className="text-center text-xs mt-6" style={{ color: '#94a3b8' }}>
          © {new Date().getFullYear()} TTS360 — Activenet Operations Platform
        </p>
      </div>
    </main>
  );
}
